import * as yup from 'yup'
import { useContext, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import { yupResolver } from '@hookform/resolvers/yup'
import Header from '@components/configurations/header'
import { useForm, FormProvider } from 'react-hook-form'
import CreateUsersForm from '@components/configurations/users_config/users/form/createUsersForm'
import PermissionSetting from '@components/configurations/users_config/users/form/permissionSetting'
import { Box, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import useAPIFetch from '@hooks/useAPIFetch'

import UserService from '@services/users/user.service'
import { InitUserInfo, UserInfo } from '@models/user.model'
import ValidationToast from '@components/configurations/form/ValidationToast'
import { useMeta } from '@contexts/Meta'
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded'
import { hasPermission } from '@utils/permission_helper'
import { PermissionCodeEnum } from '@models/permission'
import { AuthContext } from '@contexts/Auth'

export const CreateUsers = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { setRequest, isLoading } = useAPIFetch()
	const { state: authState } = useContext(AuthContext)

	const validationSchema = yup
		.object()
		.shape({
			email: yup.string().email().required('Email is required'),
			first_name: yup.string().max(255).trim().required('First Name is required'),
			last_name: yup.string().max(255).trim().required('Last Name is required'),
			company: yup.string().max(255),
			position: yup.string().max(255),
			phone: yup.string().max(50),
		})
		.required()

	const methods = useForm<UserInfo>({
		defaultValues: { ...InitUserInfo },
		resolver: yupResolver(validationSchema),
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const { handleSubmit, control } = methods

	const onSubmit = async (data: any) => {
		console.info('[Create User -  payload]: ', data)
		setRequest({
			callback: async (cancelToken: any) => {
				await UserService.CreateUserWithRecovery(data).then(() => {
					enqueueSnackbar(t('User created successfully'), {
						variant: 'success',
					})
					navigate(-1)
				})
			},
		})
	}

	const onError = (errors: any) => {
		console.log('errors', errors)
	}

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ArrowBackIosRoundedIcon
					fontSize={'large'}
					sx={{ cursor: 'pointer', color: '#707070' }}
					onClick={() => {
						navigate(`/configurations/users_config/users`)
					}}
				/>
				<Header
					title={t('Create Users')}
					icon={<PersonIcon sx={{ fontSize: 35, color: '#707070' }} />}
				/>
			</Box>
			<Box component="form" onSubmit={handleSubmit(onSubmit, onError)} sx={{ marginX: '45px' }}>
				<FormProvider {...methods}>
					<CreateUsersForm />

					{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserAssignRole]) && (
						<PermissionSetting />
					)}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							marginTop: '12px',
						}}
					>
						<Button variant="contained" color="defaultButton" type="submit" disabled={isLoading}>
							{t('Create User')}
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={() => navigate(-1)}
							disabled={isLoading}
						>
							{t('Cancel')}
						</Button>
					</Box>
				</FormProvider>
				<ValidationToast control={control} />
			</Box>
		</>
	)
}
